import { useState, useMemo } from 'react';
import { ArrowLeft, Search, MessageSquare, Archive, MoreVertical, Pencil, Trash2, ArchiveRestore, X } from 'lucide-react';
import { Conversation } from '@kifuerpflege/shared';
import { useConversationStore } from '../../stores/conversationStore';
import { useUIStore } from '../../stores/uiStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '@/lib/utils';

export function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const { conversations, selectConversation, activeConversationId, updateConversation, deleteConversation } =
    useConversationStore();
  const { setSidebarOpen, setCurrentView } = useUIStore();

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    const query = searchQuery.toLowerCase();
    return conversations.filter((conv) =>
      conv.title.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  const handleSelectChat = (conversationId: string) => {
    selectConversation(conversationId);
    setCurrentView('chat');
    setSidebarOpen(false);
    setSearchQuery('');
  };

  const openRenameDialog = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setNewTitle(conversation.title);
    setRenameOpen(true);
  };

  const openDeleteDialog = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setDeleteOpen(true);
  };

  const handleRename = async () => {
    if (!selectedConversation || !newTitle.trim() || newTitle === selectedConversation.title) {
      setRenameOpen(false);
      return;
    }

    try {
      await updateConversation(selectedConversation.id, { title: newTitle.trim() });
      setRenameOpen(false);
      setSelectedConversation(null);
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
  };

  const handleArchiveToggle = async (conversation: Conversation) => {
    try {
      await updateConversation(conversation.id, { isArchived: !conversation.isArchived });
    } catch (error) {
      console.error('Failed to update conversation:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedConversation) return;

    try {
      await deleteConversation(selectedConversation.id);
      setDeleteOpen(false);
      setSelectedConversation(null);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCurrentView('chat')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-semibold">Chat suchen</h1>
      </div>

      {/* Search Input */}
      <div className="p-4 pb-2">
        <div className="relative max-w-2xl mx-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Chat suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn('pl-9', searchQuery && 'pr-9')}
            autoFocus
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Chat List */}
      <ScrollArea className="flex-1">
        <div className="max-w-2xl mx-auto p-4 space-y-1">
          {filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              {searchQuery ? 'Keine Chats gefunden.' : 'Noch keine Chats vorhanden.'}
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                className={cn(
                  'group flex items-center gap-3 px-3 py-2 rounded-lg transition-colors cursor-pointer',
                  conversation.id === activeConversationId
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
                onClick={() => handleSelectChat(conversation.id)}
              >
                {conversation.isArchived ? (
                  <Archive className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <MessageSquare className="h-4 w-4 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{conversation.title}</p>
                    {conversation.isArchived && (
                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground shrink-0">
                        Archiviert
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(conversation.createdAt).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </p>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openRenameDialog(conversation); }}>
                      <Pencil className="mr-2 h-4 w-4" />
                      <span>Umbenennen</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleArchiveToggle(conversation); }}>
                      {conversation.isArchived ? (
                        <>
                          <ArchiveRestore className="mr-2 h-4 w-4" />
                          <span>Wiederherstellen</span>
                        </>
                      ) : (
                        <>
                          <Archive className="mr-2 h-4 w-4" />
                          <span>Archivieren</span>
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); openDeleteDialog(conversation); }}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Löschen</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Chat umbenennen</DialogTitle>
          </DialogHeader>
          <div className="py-4 px-1">
            <div className="space-y-2">
              <Label htmlFor="searchChatTitle">Titel</Label>
              <Input
                id="searchChatTitle"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Chat-Titel eingeben"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRename();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleRename}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Chat löschen</DialogTitle>
          </DialogHeader>
          <div className="py-4 px-4">
            <p className="text-muted-foreground">
              Möchten Sie den Chat "{selectedConversation?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
